if (!customElements.get('aa-community')) {
  customElements.define(
    'aa-community',
    class extends HTMLElement {
      connectedCallback() {
        const pausedByVisitor = !!this.hold?.button;
        if (this.ac) this.disconnectedCallback();
        this.track = this.querySelector('.aa-comm__track');
        this.row = this.querySelector('.aa-comm__row');
        this.items = this.row ? [...this.row.children].filter((li) => !li.classList.contains('aa-comm__item--clone')) : [];
        if (!this.track || !this.items.length) return;

        this.ac = new AbortController();
        const on = (el, type, fn, options) => el.addEventListener(type, fn, { ...options, signal: this.ac.signal });
        this.hold = { button: pausedByVisitor, focus: this.track.contains(document.activeElement) };
        this.visible = !('IntersectionObserver' in window);
        this.ioSeen = false;
        this.mediaReady = false;
        this.warming = false;
        this.reduce = matchMedia('(prefers-reduced-motion: reduce)');

        const t = this.track;
        const set = (key, value) => { this.hold[key] = value; this.update(); };
        on(t, 'pointerenter', (event) => event.pointerType !== 'touch' && set('hover', true));
        on(t, 'pointerleave', (event) => event.pointerType !== 'touch' && set('hover', false));
        on(t, 'pointerdown', (event) => set(event.pointerType === 'touch' ? 'touch' : 'hover', true));
        on(t, 'focusin', (event) => {
          set('focus', true);
          const item = event.target.closest('.aa-comm__item');
          if (item && event.target.matches(':focus-visible')) this.reveal(item);
        });
        on(t, 'focusout', (event) => set('focus', t.contains(event.relatedTarget)));
        on(t, 'keydown', (event) => {
          if (event.target !== t || event.code !== 'Space' || event.repeat) return;
          event.preventDefault();
          set('button', !this.hold.button);
        });

        let finger = false;
        const release = () => {
          clearTimeout(this.touchT);
          this.touchT = setTimeout(() => set('touch', false), 2000);
        };
        on(t, 'touchstart', () => { finger = true; clearTimeout(this.touchT); set('touch', true); }, { passive: true });
        const lift = () => { finger = false; release(); };
        on(t, 'touchend', lift, { passive: true });
        on(t, 'touchcancel', lift, { passive: true });
        on(t, 'scroll', () => { if (this.hold.touch && !finger) release(); }, { passive: true });
        this.dragInit(on);
        on(this, 'shopify:block:select', (event) => {
          set('editor', true);
          event.target.scrollIntoView({ block: 'nearest', inline: 'start' });
        });
        on(this, 'shopify:block:deselect', () => set('editor', false));
        on(document, 'visibilitychange', () => this.update());
        on(this.reduce, 'change', () => this.setup());

        if (!this.visible) {
          this.io = new IntersectionObserver((entries) => {
            this.ioSeen = true;
            this.visible = entries[entries.length - 1].isIntersecting;
            this.update();
          });
          this.io.observe(this);
          this.ioT = setTimeout(() => {
            if (!this.ioSeen) { this.visible = true; this.update(); }
          }, 1500);
        }
        this.setup();
        this.ro = new ResizeObserver(() => { this.measure(); this.update(); });
        this.ro.observe(t);
      }

      disconnectedCallback() {
        this.visible = false;
        this.stop();
        this.ac?.abort();
        this.ac = null;
        this.io?.disconnect();
        this.ro?.disconnect();
        this.io = this.ro = null;
        clearTimeout(this.ioT);
        clearTimeout(this.touchT);
        clearTimeout(this.dragT);
      }

      reveal(item) {
        const t = this.track, tr = t.getBoundingClientRect(), rect = item.getBoundingClientRect();
        const left = Math.max(tr.left, 0), right = Math.min(tr.right, document.documentElement.clientWidth);
        if (rect.left < left) t.scrollLeft -= left - rect.left;
        else if (rect.right > right) t.scrollLeft += rect.right - right;
      }

      dragInit(on) {
        const t = this.track;
        let x0 = 0, scroll0 = 0, id = null, moved = false;
        on(t, 'dragstart', (event) => event.preventDefault());
        on(t, 'pointerdown', (event) => {
          if (event.pointerType !== 'mouse' || event.button !== 0) return;
          id = event.pointerId; x0 = event.clientX; scroll0 = t.scrollLeft; moved = false;
        });
        on(t, 'pointermove', (event) => {
          if (event.pointerId !== id) return;
          const dx = event.clientX - x0;
          if (!moved && Math.abs(dx) < 5) return;
          if (!moved) { moved = true; t.setPointerCapture(id); this.hold.drag = true; this.update(); }
          t.scrollLeft = scroll0 - dx;
        });
        const end = (event) => {
          if (event.pointerId !== id) return;
          id = null;
          if (!moved) return;
          this.hold.drag = false;
          const swallow = (click) => { click.preventDefault(); click.stopPropagation(); };
          on(t, 'click', swallow, { capture: true, once: true });
          this.dragT = setTimeout(() => {
            t.removeEventListener('click', swallow, { capture: true });
            if (this.ac) this.update();
          }, 0);
        };
        on(t, 'pointerup', end);
        on(t, 'pointercancel', end);
      }

      setup() {
        this.stop();
        const position = this.track.scrollLeft, previousWidth = this.setW, wasAuto = this.auto;
        this.row.querySelectorAll('.aa-comm__item--clone').forEach((copy) => copy.remove());
        this.auto = !this.reduce.matches && typeof this.row.animate === 'function';
        this.classList.toggle('is-auto', this.auto);
        if (this.auto) {
          this.measure();
          if (wasAuto && previousWidth > 0) this.track.scrollLeft = ((position / previousWidth % 1) + 1) % 1 * this.setW;
          else this.track.scrollLeft = position;
        }
        this.update();
      }

      measure() {
        if (!this.auto) return;
        this.stop();
        const t = this.track, row = this.row;
        const progress = this.setW > 0 ? t.scrollLeft / this.setW : 0;
        const addSet = () => {
          const fragment = document.createDocumentFragment();
          this.items.forEach((item) => {
            const copy = item.cloneNode(true);
            copy.classList.add('aa-comm__item--clone');
            copy.removeAttribute('data-shopify-editor-block');
            copy.setAttribute('aria-hidden', 'true');
            copy.querySelectorAll('a').forEach((link) => { link.tabIndex = -1; });
            fragment.append(copy);
          });
          row.append(fragment);
        };
        if (!row.querySelector('.aa-comm__item--clone')) addSet();
        const copy = row.querySelector('.aa-comm__item--clone');
        this.setW = copy.getBoundingClientRect().left - this.items[0].getBoundingClientRect().left;
        if (this.setW <= 0) return;
        for (let i = 0; i < 20 && row.scrollWidth - this.setW < t.clientWidth + 1; i++) addSet();
        this.duration = this.items.length * (parseFloat(this.dataset.autoplay) || 8) * 1000;
        t.scrollLeft = ((progress % 1) + 1) % 1 * this.setW;
      }

      warmImages() {
        if (this.warming || this.mediaReady) return;
        this.warming = true;
        const connection = this.ac;
        this.row.querySelectorAll('img').forEach((image) => { image.loading = 'eager'; });
        const images = this.items.flatMap((item) => [...item.querySelectorAll('img')]);
        Promise.allSettled(images.map((image) => image.decode?.())).then(() => {
          if (this.ac !== connection) return;
          this.mediaReady = true;
          this.warming = false;
          this.update();
        });
      }

      update() {
        if (!this.ac) return;
        if (this.auto && this.visible && !document.hidden) this.warmImages();
        const h = this.hold;
        const paused = h.hover || h.focus || h.touch || h.drag || h.button || h.editor;
        const run = this.auto && this.visible && !document.hidden && this.mediaReady && !paused && this.setW > 0;
        this.toggleAttribute('data-running', !!run);
        if (run && !this.animation) this.start();
        else if (!run) this.stop();
      }

      start() {
        const position = ((this.track.scrollLeft % this.setW) + this.setW) % this.setW;
        this.animation = this.row.animate(
          [{ transform: 'translate3d(0,0,0)' }, { transform: 'translate3d(' + -this.setW + 'px,0,0)' }],
          { duration: this.duration, iterations: Infinity, easing: 'linear' }
        );
        this.animation.pause();
        this.animation.currentTime = position / this.setW * this.duration;
        this.track.scrollLeft = 0;
        this.animation.play();
      }

      stop() {
        if (!this.animation) return;
        const time = Number(this.animation.currentTime) || 0;
        const position = ((time / this.duration % 1) + 1) % 1 * this.setW;
        this.animation.cancel();
        this.animation = null;
        this.track.scrollLeft = position;
      }
    }
  );
}
